/**
 * BookingPaymentSplitDialog
 *
 * Booking-level "Add payment": collect an amount against the folio balance and
 * either charge a single payer or split it between guests (Equal / Percent /
 * Custom €), each portion attributed to its payer. Everything is submitted as a
 * split (single payer = one split row) via POST /bookings/{id}/settle_split/.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Banknote, CreditCard, ArrowLeftRight, Wifi, MoreHorizontal } from 'lucide-react';
import { useTranslation } from '@/core';
import { useLocationCurrency } from '@/shared/hooks/useLocationCurrency';
import { Button } from '@/theme/components/ui/button';
import { Input } from '@/theme/components/ui/input';
import { Label } from '@/theme/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/theme/components/ui/dialog';
import { ScrollArea } from '@/theme/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { FolioPaymentMethod, SettleBookingSplitInput } from '../../types';

export interface SplitGuest {
  /** Person id — the payer attribution (paid_by_person). */
  personId: string;
  name: string;
  isOwner?: boolean;
}

interface BookingPaymentSplitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Max collectable against the folio (the amount-based bucket). */
  balanceDue: number;
  guests: SplitGuest[];
  onSubmit: (input: SettleBookingSplitInput) => void;
  isLoading?: boolean;
}

type Mode = 'single' | 'split';
type SplitMode = 'even' | 'pct' | 'custom';

const METHOD_OPTIONS: { value: FolioPaymentMethod; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'card', icon: CreditCard },
  { value: 'cash', icon: Banknote },
  { value: 'transfer', icon: ArrowLeftRight },
  { value: 'guest_prepaid', icon: Wifi },
  { value: 'other', icon: MoreHorizontal },
];

const round2 = (n: number) => Math.round(n * 100) / 100;

interface RowState {
  included: boolean;
  amount: number;
  pct: number;
  method: FolioPaymentMethod;
}

export function BookingPaymentSplitDialog({
  open, onOpenChange, balanceDue, guests, onSubmit, isLoading = false,
}: BookingPaymentSplitDialogProps) {
  const { t } = useTranslation();
  const { formatPrice, activeCurrency, numberLocale } = useLocationCurrency();

  // The amount input needs the bare symbol, which `formatPrice` never yields on
  // its own. Pulled off Intl so a GBP location gets "£", not a hardcoded "€".
  const currencySymbol = useMemo(() => {
    try {
      return (
        new Intl.NumberFormat(numberLocale, { style: 'currency', currency: activeCurrency.code })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value
        ?? activeCurrency.symbol
        ?? activeCurrency.code
      );
    } catch {
      return activeCurrency.symbol ?? activeCurrency.code;
    }
  }, [activeCurrency, numberLocale]);

  const [amount, setAmount] = useState<number>(round2(balanceDue));
  const [mode, setMode] = useState<Mode>('single');
  const [splitMode, setSplitMode] = useState<SplitMode>('even');
  const [singlePayer, setSinglePayer] = useState<string>('external');
  const [singleName, setSingleName] = useState('');
  const [singleMethod, setSingleMethod] = useState<FolioPaymentMethod>('card');
  // Per-payer state is keyed by personId, never by index: the dialog is mounted
  // by its parent before `guests` resolves (the guest query only starts once
  // the booking detail has landed), so an index-seeded array would freeze empty
  // and every render after that would read past its end.
  const [rowByPerson, setRowByPerson] = useState<Record<string, RowState>>({});

  const rows = useMemo<RowState[]>(
    () =>
      guests.map(
        (g, i) =>
          rowByPerson[g.personId] ?? {
            included: i < 2,
            amount: 0,
            pct: 0,
            method: 'card' as FolioPaymentMethod,
          },
      ),
    [guests, rowByPerson],
  );

  // The parent keeps this dialog mounted, so the amount has to be re-seeded on
  // every open — otherwise a second payment prefills the balance from before
  // the first one and `amountValid` rejects it.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) setAmount(round2(balanceDue));
    wasOpen.current = open;
  }, [open, balanceDue]);

  // Recompute per-row amounts for Equal / Percent (Custom keeps typed values).
  const computedRows = useMemo(() => {
    if (splitMode === 'custom') return rows;
    const included = rows.filter((r) => r.included);
    if (splitMode === 'even') {
      const per = included.length ? round2(amount / included.length) : 0;
      const next = rows.map((r) => ({ ...r, amount: r.included ? per : 0 }));
      // absorb rounding drift on the last included row
      const sum = next.reduce((a, r) => a + r.amount, 0);
      const drift = round2(amount - sum);
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].included) { next[i] = { ...next[i], amount: round2(next[i].amount + drift) }; break; }
      }
      return next;
    }
    // percent
    return rows.map((r) => ({ ...r, amount: r.included ? round2((amount * (r.pct || 0)) / 100) : 0 }));
  }, [rows, splitMode, amount]);

  const allocated = computedRows.reduce((a, r) => a + (r.included ? r.amount : 0), 0);
  const remaining = round2(amount - allocated);
  const anyIncluded = computedRows.some((r) => r.included);

  const amountValid = amount > 0 && amount <= round2(balanceDue) + 0.001;
  const canSubmit = mode === 'single'
    ? amountValid
    : amountValid && anyIncluded && Math.abs(remaining) < 0.005;

  /** Persist the whole derived row list back under its payer keys. */
  const commitRows = (next: RowState[]) =>
    setRowByPerson(Object.fromEntries(guests.map((g, i) => [g.personId, next[i]])));

  const setRow = (i: number, patch: Partial<RowState>) =>
    commitRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const chooseSplitMode = (sm: SplitMode) => {
    if (sm === 'pct') {
      const inc = rows.filter((r) => r.included).length || 1;
      commitRows(rows.map((r) => ({ ...r, pct: r.included ? Math.round(100 / inc) : 0 })));
    }
    setSplitMode(sm);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const splits = mode === 'single'
      ? [{
          amount: round2(amount),
          method: singleMethod,
          ...(singlePayer === 'external'
            ? { paidByName: singleName.trim() || undefined }
            : { paidByPersonId: singlePayer }),
        }]
      : computedRows
          .map((r, i) => ({ r, g: guests[i] }))
          .filter(({ r }) => r.included && r.amount > 0)
          .map(({ r, g }) => ({ amount: round2(r.amount), method: r.method, paidByPersonId: g.personId }));
    onSubmit({ amount: round2(amount), splits });
  };

  const handleOpenChange = (v: boolean) => {
    if (isLoading) return;
    onOpenChange(v);
  };

  const amountPct = balanceDue > 0 ? Math.round((amount / balanceDue) * 100) : 0;
  const AMOUNT_CHIPS: { label: string; value: number }[] = [
    { label: t('bookingSplit.full', 'Full'), value: round2(balanceDue) },
    { label: t('bookingSplit.half', 'Half'), value: round2(balanceDue / 2) },
    { label: t('bookingSplit.deposit30', '30%'), value: round2(balanceDue * 0.3) },
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('bookingSplit.title', 'Record payment')}</DialogTitle>
          <DialogDescription>
            {t('bookingSplit.balanceDue', 'Balance due')}{' '}
            <span className="font-mono font-semibold tabular-nums">{formatPrice(balanceDue)}</span>
            {' · '}{t('bookingSplit.sharedFolio', 'shared folio')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Amount */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('bookingSplit.amountToCollect', 'Amount to collect')}
            </Label>
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xl text-muted-foreground">{currencySymbol}</span>
                <Input
                  inputMode="decimal"
                  value={String(amount)}
                  onChange={(e) => setAmount(round2(parseFloat(e.target.value) || 0))}
                  disabled={isLoading}
                  className="border-0 bg-transparent px-0 font-mono text-2xl font-semibold tabular-nums focus-visible:ring-0"
                />
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{amountPct}%</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {AMOUNT_CHIPS.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => setAmount(c.value)}
                    disabled={isLoading}
                    className={cn(
                      'rounded-full border px-3 py-1 font-mono text-xs transition-colors',
                      Math.abs(amount - c.value) < 0.005
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50',
                    )}
                  >
                    {c.label} {formatPrice(c.value)}
                  </button>
                ))}
              </div>
            </div>
            {!amountValid && amount > 0 && (
              <p className="text-xs text-destructive">
                {t('bookingSplit.amountExceeds', 'Amount exceeds the collectable balance.')}
              </p>
            )}
          </div>

          {/* Mode */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('bookingSplit.whoPays', 'Who pays')}
            </Label>
            <div className="grid grid-cols-2 gap-1 rounded-md border bg-muted/30 p-1">
              {(['single', 'split'] as Mode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  disabled={isLoading}
                  className={cn(
                    'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                    mode === m ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground',
                  )}
                >
                  {m === 'single' ? t('bookingSplit.singlePayer', 'Single payer') : t('bookingSplit.split', 'Split between guests')}
                </button>
              ))}
            </div>
          </div>

          {/* Single payer */}
          {mode === 'single' && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <select
                  value={singlePayer}
                  onChange={(e) => setSinglePayer(e.target.value)}
                  disabled={isLoading}
                  className="flex-1 rounded-md border bg-background px-2 py-2 text-sm"
                >
                  {guests.map((g) => (
                    <option key={g.personId} value={g.personId}>
                      {g.name}{g.isOwner ? ` (${t('common.owner', 'Owner')})` : ''}
                    </option>
                  ))}
                  <option value="external">{t('bookingSplit.externalPayer', 'External payer…')}</option>
                </select>
              </div>
              {singlePayer === 'external' && (
                <Input
                  placeholder={t('folio.paidByNamePlaceholder', 'Payer name')}
                  value={singleName}
                  onChange={(e) => setSingleName(e.target.value)}
                  disabled={isLoading}
                />
              )}
              <MethodPicker value={singleMethod} onChange={setSingleMethod} disabled={isLoading} />
            </div>
          )}

          {/* Split */}
          {mode === 'split' && (
            <div className="space-y-2">
              <div className="flex gap-1.5">
                {(['even', 'pct', 'custom'] as SplitMode[]).map((sm) => (
                  <button
                    key={sm}
                    type="button"
                    onClick={() => chooseSplitMode(sm)}
                    disabled={isLoading}
                    className={cn(
                      'rounded-md border px-3 py-1 text-xs font-medium transition-colors',
                      splitMode === sm ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted',
                    )}
                  >
                    {sm === 'even'
                      ? t('bookingSplit.equal', 'Equal')
                      : sm === 'pct'
                        ? t('bookingSplit.percent', 'Percent')
                        : t('bookingSplit.customAmount', 'Custom {{symbol}}', { symbol: currencySymbol })}
                  </button>
                ))}
              </div>

              <ScrollArea className="max-h-56">
                <div className="space-y-1.5 pr-2">
                  {guests.map((g, i) => {
                    const r = computedRows[i];
                    return (
                      <div
                        key={g.personId}
                        className={cn('flex items-center gap-2 rounded-md border p-2', !r.included && 'opacity-50')}
                      >
                        <button
                          type="button"
                          onClick={() => setRow(i, { included: !r.included })}
                          disabled={isLoading}
                          aria-pressed={r.included}
                          className={cn(
                            'grid h-5 w-5 place-items-center rounded border text-xs text-primary-foreground',
                            r.included ? 'border-primary bg-primary' : 'border-input',
                          )}
                        >
                          {r.included ? '✓' : ''}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{g.name}</div>
                          <div className="text-[11px] text-muted-foreground">{g.isOwner ? t('common.owner', 'Owner') : t('guest.title', 'Guest')}</div>
                        </div>
                        {splitMode === 'pct' ? (
                          <div className="flex items-center gap-1">
                            <Input
                              inputMode="decimal"
                              value={String(r.pct || 0)}
                              onChange={(e) => setRow(i, { pct: parseFloat(e.target.value) || 0 })}
                              disabled={isLoading || !r.included}
                              className="h-8 w-16 text-right font-mono text-sm"
                            />
                            <span className="font-mono text-xs text-muted-foreground">%</span>
                          </div>
                        ) : (
                          <Input
                            inputMode="decimal"
                            value={r.amount.toFixed(2)}
                            readOnly={splitMode === 'even'}
                            onChange={(e) => splitMode === 'custom' && setRow(i, { amount: round2(parseFloat(e.target.value) || 0) })}
                            disabled={isLoading || !r.included}
                            className="h-8 w-24 text-right font-mono text-sm"
                          />
                        )}
                        <select
                          value={r.method}
                          onChange={(e) => setRow(i, { method: e.target.value as FolioPaymentMethod })}
                          disabled={isLoading || !r.included}
                          className="h-8 rounded-md border bg-background px-1 text-xs"
                        >
                          {METHOD_OPTIONS.map((m) => (
                            <option key={m.value} value={m.value}>{t(`folio.method.${m.value}`)}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              <div
                className={cn(
                  'flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium',
                  Math.abs(remaining) < 0.005 && anyIncluded
                    ? 'bg-success/10 text-success'
                    : 'bg-warning/10 text-warning',
                )}
              >
                <span>
                  {Math.abs(remaining) < 0.005 && anyIncluded
                    ? t('bookingSplit.fullyAllocated', 'Fully allocated')
                    : remaining < 0
                      ? t('bookingSplit.overAllocated', 'Over-allocated')
                      : t('bookingSplit.remaining', 'Remaining to allocate')}
                </span>
                <span className="font-mono tabular-nums">{formatPrice(Math.abs(remaining))}</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isLoading}>
            {isLoading
              ? t('common.processing', 'Processing…')
              : mode === 'split'
                ? t('bookingSplit.recordN', 'Record {{count}} payments · {{total}}', {
                    count: computedRows.filter((r) => r.included && r.amount > 0).length,
                    total: formatPrice(amount),
                  })
                : `${t('bookingSplit.record', 'Record')} ${formatPrice(amount)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MethodPicker({
  value, onChange, disabled,
}: {
  value: FolioPaymentMethod;
  onChange: (m: FolioPaymentMethod) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-5 gap-1">
      {METHOD_OPTIONS.map(({ value: v, icon: Icon }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          disabled={disabled}
          className={cn(
            'flex flex-col items-center gap-1 rounded-md border p-2 text-xs transition-colors',
            value === v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted',
          )}
        >
          <Icon className="h-4 w-4" />
          {t(`folio.method.${v}`)}
        </button>
      ))}
    </div>
  );
}
